/**
 * Funds — cash, margin and collateral, proxied from /brokers/funds.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, IndianRupee, Layers, RefreshCw, TrendingUp, Wallet } from 'lucide-react';
import { api, getErrorMessage } from '@/config/api';
import { formatINR, formatPct, signClass, signOf, toNum } from '@/lib/format';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  PageHeader,
  StatCard,
} from '@/components/ui';

interface FundsData {
  available_cash: string;
  used_margin: string;
  available_margin: string;
  total_collateral: string;
  mtm: string;
}

interface ConnectionInfo {
  connection_id: string;
  broker_type: string;
  display_name: string;
  ucc: string;
  is_active: boolean;
  is_session_valid: boolean;
  session_expires_at: string | null;
  last_connected_at: string | null;
}

const DEMO_FUNDS: FundsData = {
  available_cash: '485000.00',
  used_margin: '115000.00',
  available_margin: '370000.00',
  total_collateral: '250000.00',
  mtm: '18500.00',
};

export default function FundsPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connections, setConnections] = useState<ConnectionInfo[]>([]);
  const [funds, setFunds] = useState<FundsData | null>(null);
  const [showDemo, setShowDemo] = useState(false);

  const activeConnection = connections.find((c) => c.is_active && c.is_session_valid);

  const fetchFunds = async (connectionId?: string) => {
    try {
      setError(null);
      const url = connectionId ? `/brokers/funds?connection_id=${connectionId}` : '/brokers/funds';
      const { data } = await api.get(url);
      setFunds(data);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const initData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/brokers/status');
      setConnections(data);
      const active = data.find((c: ConnectionInfo) => c.is_active && c.is_session_valid);
      if (active) await fetchFunds(active.connection_id);
    } catch {
      setError('Failed to fetch connection status');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (!activeConnection && !showDemo) return;
    setRefreshing(true);
    try {
      if (showDemo) {
        await new Promise((resolve) => setTimeout(resolve, 800));
      } else if (activeConnection) {
        await fetchFunds(activeConnection.connection_id);
      }
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    initData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentFunds = showDemo ? DEMO_FUNDS : funds;
  const connected = Boolean(activeConnection);

  const utilisation = useMemo(() => {
    const used = toNum(currentFunds?.used_margin);
    const available = toNum(currentFunds?.available_margin);
    const total = used + available;
    return {
      used,
      available,
      total,
      pct: total > 0 ? Math.round((used / total) * 100) : 0,
    };
  }, [currentFunds]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={Wallet}
        title="Funds & margins"
        description="Cash availability, margin utilisation and collateral"
        meta={
          showDemo ? (
            <Badge tone="warning">Sample data</Badge>
          ) : connected ? (
            <Badge tone="success">{activeConnection?.display_name ?? 'Connected'}</Badge>
          ) : (
            <Badge tone="neutral">Not connected</Badge>
          )
        }
        actions={
          <>
            <Button
              variant={showDemo ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setShowDemo((v) => !v)}
              aria-pressed={showDemo}
            >
              {showDemo ? 'Showing sample data' : 'Show sample data'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              icon={RefreshCw}
              loading={refreshing}
              disabled={!connected && !showDemo}
              onClick={handleRefresh}
            >
              Refresh
            </Button>
          </>
        }
      />

      {error && (
        <Alert
          tone="danger"
          title="Couldn't load funds"
          action={
            <Button variant="secondary" size="sm" onClick={initData}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {!connected && !loading && !showDemo && (
        <Alert
          tone="warning"
          title="No broker connected"
          action={
            <Button variant="secondary" size="sm" iconRight={ArrowRight} onClick={() => navigate('/settings')}>
              Connect
            </Button>
          }
        >
          Connect Kotak Neo in Settings to see live limits, or switch on sample data to explore the screen.
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Available cash"
          value={formatINR(currentFunds?.available_cash)}
          icon={IndianRupee}
          loading={loading}
        />
        <StatCard
          label="Available margin"
          value={formatINR(currentFunds?.available_margin)}
          icon={Wallet}
          loading={loading}
          hint={`${100 - utilisation.pct}% of limit free`}
        />
        <StatCard
          label="Used margin"
          value={formatINR(currentFunds?.used_margin)}
          icon={Layers}
          loading={loading}
          hint={`${utilisation.pct}% utilised`}
        />
        <StatCard
          label="Mark to market"
          value={<span className={signClass(currentFunds?.mtm)}>{formatINR(currentFunds?.mtm)}</span>}
          icon={TrendingUp}
          loading={loading}
          delta={{ value: 'today', direction: signOf(currentFunds?.mtm) }}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title="Margin utilisation" description="Used against total available limit" />
          <CardBody className="flex flex-col gap-4">
            <div>
              <div className="flex items-baseline justify-between">
                <span className="font-tabular text-2xl font-semibold text-fg">{utilisation.pct}%</span>
                <span className="font-tabular text-xs text-fg-subtle">
                  {formatINR(utilisation.used)} / {formatINR(utilisation.total)}
                </span>
              </div>
              <div
                className="mt-3 h-2 w-full overflow-hidden rounded-full bg-surface-3"
                role="progressbar"
                aria-valuenow={utilisation.pct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Margin utilisation"
              >
                <div
                  className="h-full rounded-full bg-brand transition-[width] duration-300"
                  style={{ width: `${Math.min(utilisation.pct, 100)}%` }}
                />
              </div>
            </div>

            <dl className="grid grid-cols-2 gap-4 border-t border-line pt-4 text-sm">
              <div>
                <dt className="text-xs text-fg-faint">Used</dt>
                <dd className="font-tabular text-fg-muted">{formatINR(utilisation.used)}</dd>
              </div>
              <div>
                <dt className="text-xs text-fg-faint">Free</dt>
                <dd className="font-tabular text-fg-muted">{formatINR(utilisation.available)}</dd>
              </div>
            </dl>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Capital breakdown" description="Where your buying power comes from" />
          <CardBody>
            <dl className="flex flex-col divide-y divide-line">
              {[
                { label: 'Available cash', value: currentFunds?.available_cash, hint: 'Settled funds ready to deploy' },
                { label: 'Total collateral', value: currentFunds?.total_collateral, hint: 'Pledged holdings value' },
                { label: 'Available margin', value: currentFunds?.available_margin, hint: 'Cash plus collateral, less usage' },
                { label: 'Used margin', value: currentFunds?.used_margin, hint: 'Blocked against open positions' },
              ].map((row) => (
                <div key={row.label} className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0">
                  <div>
                    <dt className="text-sm text-fg-muted">{row.label}</dt>
                    <dd className="text-xs text-fg-faint">{row.hint}</dd>
                  </div>
                  <span className="font-tabular text-sm font-medium whitespace-nowrap text-fg">
                    {formatINR(row.value)}
                  </span>
                </div>
              ))}
            </dl>
          </CardBody>
        </Card>
      </div>

      {currentFunds && (
        <p className="text-xs text-fg-faint">
          MTM {formatPct(
            toNum(currentFunds.available_margin) > 0
              ? (toNum(currentFunds.mtm) / toNum(currentFunds.available_margin)) * 100
              : 0
          )}{' '}
          of available margin.
        </p>
      )}
    </div>
  );
}
